import { getUserSessionCookie } from '../utils/SessionCookie';
import { apiFetch } from './ApiFetch';
import { throwIfHttpError } from './httpErrorMessage';
import { BASE_URL } from './constants';

export type ScheduleType = 'daily' | 'weekly' | 'monthly';

/** Backend enum values for job_type. */
export type ScheduledJobType =
  | 'campaign_prospect_ingestion'
  | 'campaign_cell_execution'
  | 'campaign_crm_synchronization';

/** Request body for creating a scheduled job. */
export interface ScheduledJobCreate {
  job_type: ScheduledJobType;
  job_config: Record<string, unknown>;
  schedule_type: ScheduleType;
}

/** Request body for updating a scheduled job (partial). */
export interface ScheduledJobUpdate {
  job_config?: Record<string, unknown>;
  schedule_type?: ScheduleType;
}

/** Base response for a scheduled job. */
export interface ScheduledJobResponse {
  id: string;
  campaign_id: string;
  job_type: ScheduledJobType;
  job_config: Record<string, unknown>;
  schedule_type: string;
  run_count: number;
  create_ts: string;
  update_ts?: string;
}

/** HubSpot import scheduled job source_config (companies only). */
export interface HubSpotImportSourceConfig {
  import_mapping: Array<{
    hubspot_property: string;
    campaign_column: string;
    is_primary: boolean;
  }>;
  scope: 'all' | 'my_companies' | 'company_list';
  hubspot_owner_id?: string;
  company_ids?: string[];
}

/** Source job with InputAdapterConfig. */
export interface SourceJobResponse {
  id: string;
  campaign_id: string;
  schedule_type: string;
  run_count: number;
  create_ts: string;
  update_ts?: string;
  config: {
    source_type?: string;
    source_config?: Record<string, unknown>;
    column_mapping?: Array<{ workflow_column: string; adapter_column: string }>;
    [key: string]: unknown;
  };
}

/** Cell executor job. */
export interface CellExecutorJobResponse {
  id: string;
  campaign_id: string;
  schedule_type: string;
  run_count: number;
  create_ts: string;
  update_ts?: string;
  config: Record<string, unknown>;
}

/** CRM sync job. */
export interface CRMSyncJobResponse {
  id: string;
  campaign_id: string;
  schedule_type: string;
  run_count: number;
  create_ts: string;
  update_ts?: string;
  config: Record<string, unknown>;
}

/** Response from GET all scheduled jobs, grouped by type. */
export interface ScheduledJobsByTypeResponse {
  source: SourceJobResponse[];
  cell_executor: CellExecutorJobResponse[];
  crm_sync: CRMSyncJobResponse[];
}

const SCHEDULED_JOBS_BASE = `${BASE_URL}/v1/campaigns`;

export async function getScheduledJobs(
  campaignId: string
): Promise<ScheduledJobsByTypeResponse> {
  const userSessionToken = getUserSessionCookie();
  const response = await apiFetch(
    `${SCHEDULED_JOBS_BASE}/${campaignId}/scheduled-jobs`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userSessionToken}`
      }
    }
  );
  await throwIfHttpError(response, `HTTP error! status: ${response.status}`);
  return response.json();
}

export async function createScheduledJob(
  campaignId: string,
  data: ScheduledJobCreate
): Promise<{ id: string } & ScheduledJobResponse> {
  const userSessionToken = getUserSessionCookie();
  const response = await apiFetch(
    `${SCHEDULED_JOBS_BASE}/${campaignId}/scheduled-jobs`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userSessionToken}`
      },
      body: JSON.stringify(data)
    }
  );
  await throwIfHttpError(response, `HTTP error! status: ${response.status}`);
  return response.json();
}

export async function updateScheduledJob(
  campaignId: string,
  jobId: string,
  data: ScheduledJobUpdate
): Promise<ScheduledJobResponse> {
  const userSessionToken = getUserSessionCookie();
  const response = await apiFetch(
    `${SCHEDULED_JOBS_BASE}/${campaignId}/scheduled-jobs/${jobId}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userSessionToken}`
      },
      body: JSON.stringify(data)
    }
  );
  await throwIfHttpError(response, `HTTP error! status: ${response.status}`);
  return response.json();
}

export async function deleteScheduledJob(
  campaignId: string,
  jobId: string
): Promise<void> {
  const userSessionToken = getUserSessionCookie();
  const response = await apiFetch(
    `${SCHEDULED_JOBS_BASE}/${campaignId}/scheduled-jobs/${jobId}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${userSessionToken}`
      }
    }
  );
  await throwIfHttpError(response, `HTTP error! status: ${response.status}`);
}
